import { useMutation, useQueryClient } from "react-query";
import { RequestMode } from "@/types/common";
import { ResponseUserType } from "@/types/service";
import { KeyConstant } from "@/const";
import { E2EService, RequestService } from "@/services";

type ExportRequestMutationType = {
  requestUidList: string[];
  mode: RequestMode;
  users: ResponseUserType[];
};

type UpdateRequestStatusMutationType = {
  requestUidList: string[];
  status: number;
};

export const useExportRequest = () => {
  const queryClient = useQueryClient();

  const exportRequestMutation = useMutation({
    mutationFn: (payload: ExportRequestMutationType) =>
      RequestService.exportRequest(payload.requestUidList, payload.mode),
  });

  const updateRequestStatusMutation = useMutation({
    mutationFn: (payload: UpdateRequestStatusMutationType) =>
      RequestService.updateRequestStatus(payload.requestUidList, payload.status),
  });

  const exportRequest = async (payload: ExportRequestMutationType) => {
    try {
      const userUids = payload.users.map(user => user.uid);
      const file = await exportRequestMutation.mutateAsync(payload);
      const encryptedFile = await E2EService.encryptFile(file, userUids);
      return encryptedFile;
    } catch {
      return null;
    }
  };

  const updateRequestStatus = async (payload: UpdateRequestStatusMutationType) => {
    try {
      await updateRequestStatusMutation.mutateAsync(payload);
      await queryClient.invalidateQueries({
        queryKey: [KeyConstant.REACT_QUERY.getExternalRequest],
      });
      await queryClient.invalidateQueries({
        queryKey: [KeyConstant.REACT_QUERY.getInternalRequest],
      });
      return true;
    } catch {
      return false;
    }
  };

  return { exportRequest, updateRequestStatus };
};
